import { useState } from 'react';
import { Provenance } from '../engine/DataIntervention';
import type { EvoValue, ProvenanceKind } from '../../types';
import { shortDate } from './format';

const LABEL: Record<ProvenanceKind, string> = {
  seeded_demo: 'SEED',
  public_data: 'PUB',
  connected_system: 'LIVE',
  calibrated_actuals: 'CAL',
};

const COLOR: Record<ProvenanceKind, string> = {
  seeded_demo: 'var(--gold)',
  public_data: 'var(--teal)',
  connected_system: 'var(--azura)',
  calibrated_actuals: 'var(--olive)',
};

export default function ProvenanceBadge({
  value,
  labelPrefix = '',
  labelMode = 'source',
  revealOnHover = false,
}: {
  value: EvoValue;
  labelPrefix?: string;
  labelMode?: 'source' | 'calibration';
  revealOnHover?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const pending = value.calibration_status !== 'calibrated';
  const label = labelMode === 'calibration'
    ? value.calibration_status.replace('_', ' ')
    : pending ? 'PEND' : LABEL[value.provenance];
  return (
    <span
      className="code-ref compact"
      role="button"
      tabIndex={0}
      aria-expanded={open}
      title={`${value.provenance} · ${value.calibration_status} · ${value.source_ref ?? 'source pending'} · ${shortDate(value.as_of)}`}
      onMouseEnter={() => {
        if (revealOnHover) setOpen(true);
      }}
      onMouseLeave={() => {
        if (revealOnHover) setOpen(false);
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setOpen((v) => !v);
      }}
      onBlur={() => setOpen(false)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setOpen((v) => !v);
        }
        if (event.key === 'Escape') setOpen(false);
      }}
      style={{ marginLeft: 6, whiteSpace: 'nowrap' }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 7,
          height: 7,
          borderRadius: '50%',
          marginRight: 4,
          border: `1px solid ${COLOR[value.provenance]}`,
          background: pending ? 'transparent' : COLOR[value.provenance],
        }}
      />
      <span className="code-id">{labelPrefix}{label}</span>
      {open && (
        <span className="code-detail" role="tooltip" style={{ position: 'absolute', marginTop: 22, zIndex: 50 }}>
          <b>{value.provenance.replace('_', ' ')}</b>
          <span>{value.calibration_status.replace('_', ' ')}</span>
          <span>{value.source_ref ?? 'source pending'}</span>
          <Provenance asOf={shortDate(value.as_of)} estimated={value.calibration_status !== 'calibrated'} />
        </span>
      )}
    </span>
  );
}
